import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Spinner, Textarea } from '@material-tailwind/react';
import { SaveIcon, ScrollTextIcon, AlertTriangleIcon } from 'lucide-react';
import { useGetQuotationDefaults, useUpdateQuotationDefaults } from '../../../custom-hooks/use-settings';
import { useAuth } from '../../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../../utils/enums';

const SettingsQuotation = () => {
    const { userDetails } = useAuth();
    const { data: defaults, isLoading } = useGetQuotationDefaults();
    const updateDefaultsMutation = useUpdateQuotationDefaults();

    const [inclusions, setInclusions] = useState('');
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (defaults) {
            setInclusions(defaults.inclusions || '');
            setTermsAndConditions(defaults.termsAndConditions || '');
            setNotes(defaults.notes || '');
        }
    }, [defaults]);

    const isSaving = updateDefaultsMutation.isLoading;

    const handleSave = async () => {
        const payload = {
            inclusions,
            termsAndConditions,
            notes
        };
        await updateDefaultsMutation.mutateAsync(payload);
    };

    if (isLoading) {
        return (
            <div className='min-h-[60vh] flex items-center justify-center'>
                <Spinner className='h-8 w-8' />
            </div>
        );
    }


    const hasAccess = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                      userDetails?.role === STATIC_USER_ROLES.ADMIN ||
                      (userDetails?.role === STATIC_USER_ROLES.STAFF && userDetails?.dynamicRole?.permissions?.includes('FX22-5:manage'));

    if (!hasAccess) {
        return (
            <div className='flex min-h-[60vh] flex-col items-center justify-center p-8'>
                <div className='rounded-full bg-blue-gray-50 p-6'>
                    <AlertTriangleIcon size={48} className='text-blue-gray-300' />
                </div>
                <h2 className='mt-6 text-xl font-semibold text-blue-gray-800'>No Settings Available</h2>
                <p className='mt-2 text-center text-sm text-blue-gray-500'>
                    Your current role does not have access to this settings page.
                </p>
            </div>
        );
    }

    return (
        <div className='p-4 md:p-8'>
            <div className='w-full space-y-6'>
                <div className='relative overflow-hidden rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-zinc-50 via-white to-zinc-100 p-6'>
                    <div className='absolute -top-14 -right-8 h-40 w-40 rounded-full bg-zinc-200/50 blur-2xl' />
                    <div className='relative'>
                        <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-500'>System Policy</p>
                        <h1 className='mt-1 text-2xl font-semibold text-blue-gray-900'>Quotation Settings</h1>
                        <p className='mt-2 text-sm text-blue-gray-600'>Manage the default text for new quotations.</p>
                    </div>
                </div>

                <Card className='border border-blue-gray-100 shadow-sm'>
                    <CardBody className='space-y-6'>
                        <div className='space-y-4'>
                            <div className='flex items-center gap-2 text-blue-gray-800 border-b border-blue-gray-50 pb-2'>
                                <ScrollTextIcon size={18} />
                                <h3 className='font-semibold'>Quotation Defaults</h3>
                            </div>
                            
                            <div className='space-y-6'>
                                <div>
                                    <Textarea
                                        label="Default Inclusions"
                                        value={inclusions}
                                        onChange={(e) => setInclusions(e.target.value)}
                                        rows={8}
                                        disabled={isSaving}
                                    />
                                    <p className='mt-1 text-xs text-blue-gray-500'>This text will be automatically filled into the Inclusions field for new quotations.</p>
                                </div>
                                
                                <div>
                                    <Textarea
                                        label="Default Terms & Conditions"
                                        value={termsAndConditions}
                                        onChange={(e) => setTermsAndConditions(e.target.value)}
                                        rows={5}
                                        disabled={isSaving}
                                    />
                                    <p className='mt-1 text-xs text-blue-gray-500'>This text will be automatically filled into the Terms & Conditions field for new quotations.</p>
                                </div>

                                <div>
                                    <Textarea
                                        label="Default Notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={3}
                                        disabled={isSaving}
                                    />
                                    <p className='mt-1 text-xs text-blue-gray-500'>This text will be automatically filled into the Notes field for new quotations.</p>
                                </div>
                            </div>
                        </div>

                        <div className='flex flex-col gap-3 md:flex-row pt-4'>
                            <Button
                                className='min-w-[160px] rounded-full flex items-center justify-center gap-2 bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60'
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner className='h-4 w-4' /> : <SaveIcon size={16} />}
                                Save Settings
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default SettingsQuotation;
